import React, { useState } from 'react';
import { withFormik, Field, Form, getIn } from 'formik';
import * as Yup from 'yup';
import { useDispatch } from 'react-redux';
import { Button, Row, Col, Form as BSForm } from 'react-bootstrap';
import { UploadFile, Icon, ButtonTooltip, BasicTooltip, DefaultModal } from '../../components';
import { updateRiskPreventionDocumentRequest } from '../../requests/riskPreventionDocuments';
import { sendAlert } from '../../actions/utils';

const RiskPreventionDocumentList = props => {
  const { setFieldValue, errors, touched, isSubmitting, values, index, doc, setOnRequest } = props;

  const dispatch = useDispatch();
  const [modalShow, setModalShow] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [modalBody, setModalBody] = useState('');
  const [modalAction, setModalAction] = useState(() => null);

  const { id, translatedDocumentType, documentInfo = {} } = doc || {};

  const noDocument = Object.keys(documentInfo || {}).length === 0;

  const handleUpdateRequest = checked => {
    updateRiskPreventionDocumentRequest(id, {
      dispatch,
      params: {
        risk_prevention: {
          is_public: !values.riskPrevention.isPublic
        }
      },
      formData: true,
      successCallback: () => {
        dispatch(sendAlert({ kind: 'success', message: 'Documento actualizado con éxito' }));
        setFieldValue('riskPrevention[isPublic]', checked);
      },
      callback: () => setOnRequest(false)
    });
  };

  const executeDownload = () => {
    const fileUrl = documentInfo?.fileUrl;

    if (fileUrl) {
      window.open(fileUrl, '_blank', 'noopener,noreferrer');
      dispatch(
        sendAlert({
          kind: 'success',
          message: 'Documento descargado exitosamente'
        })
      );
    } else {
      dispatch(
        sendAlert({
          kind: 'warning',
          message: 'Este documento no tiene archivo para descargar.'
        })
      );
    }
    setModalShow(false);
  };

  const downloadRiskPreventionDocument = () => {
    setModalShow(true);
    setModalTitle('Descargar Plantilla');
    setModalAction(() => executeDownload);
    setModalBody('¿Quieres descargar este documento?');
  };

  const handleModalClose = () => setModalShow(false);

  return (
    <Form>
      <Row className="align-items-center">
        <Col md={3} className="mb-2">
          <p className="mb-0 font-weight-bold text-uppercase">{translatedDocumentType}</p>
        </Col>

        <Col md={3}>
          <Field name="riskPrevention[document]">
            {({ field }) => (
              <UploadFile
                {...field}
                name={values.riskPrevention?.name || 'Adjuntar Archivo'}
                onChange={file => {
                  setFieldValue(field.name, file);
                  setFieldValue('riskPrevention[name]', file.name.split('.')[0]);
                }}
                error={getIn(errors, field.name)}
                touched={getIn(touched, field.name)}
                formats={['.pdf', '.doc', '.docx']}
                tooltipText="Formatos permitidos (DOC, DOCX, PDF)"
                size="sm"
              />
            )}
          </Field>
        </Col>

        <Col md={1} className="mb-1 px-1 d-flex align-items-center pl-3">
          <p className="futura font-weight-bold text-uppercase">Documento Público</p>
        </Col>

        <Col md={1} className="mb-4">
          <div className="d-flex mx-auto">
            <BSForm.Check
              id={`checkbox-${index}`}
              checked={values.riskPrevention.isPublic}
              className="as-checkbox me-2"
              label=""
              type="switch"
              onClick={e => setFieldValue('riskPrevention[isPublic]', e.target.checked)}
              onChange={e => !noDocument && handleUpdateRequest(e.target.checked)}
            />
            <BasicTooltip text={`Publicar ${translatedDocumentType} a los trabajadores`}>
              <Icon icon="help-circle" width={16} className="ms-1 ml-4" />
            </BasicTooltip>
          </div>
        </Col>

        <Col md={2} className="mx-auto px-3 mb-3">
          <Button
            type="submit"
            variant="primary"
            block
            disabled={isSubmitting || !values.riskPrevention.document?.size}
          >
            Guardar
          </Button>
        </Col>

        <Col md={1} className="d-flex justify-content-start mb-3">
          <ButtonTooltip
            onClick={downloadRiskPreventionDocument}
            variant="circle-primary"
            className="btn-circle mr-3"
            size="sm"
            text="Descargar plantilla"
          >
            <Icon icon="download-2" />
          </ButtonTooltip>

          <Button
            variant="circle-info"
            size="sm"
            disabled={noDocument}
            onClick={() => window.open(documentInfo?.fileUrl, '_blank', 'noopener,noreferrer')}
          >
            <Icon icon="eye" />
          </Button>
        </Col>
      </Row>

      <DefaultModal
        title={modalTitle}
        body={modalBody}
        show={modalShow}
        handleClose={handleModalClose}
        handleConfirm={modalAction}
        titleBtnClose="Cancelar"
        titleBtnSave="Confirmar"
        disabled={isSubmitting}
      />
    </Form>
  );
};

const setInitialValues = ({ doc }) => ({
  id: doc?.id,
  riskPrevention: {
    document: doc?.document,
    documentType: doc?.documentType,
    isPublic: doc?.isPublic,
    name: doc?.name
  }
});

const SUPPORTED_FORMATS = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
];

const validationSchema = Yup.object().shape({
  riskPrevention: Yup.object().shape({
    document: Yup.mixed()
      .required('Debes agregar un archivo')
      .test(
        'FILE_SIZE',
        'El archivo cargado excede el tamaño maximo permitido (10mb).',
        value => !value?.size || (value && value?.size <= 10485760)
      )
      .test(
        'FILE_FORMAT',
        'El archivo cargado tiene un formato no compatible.',
        value => !value?.type || (value && SUPPORTED_FORMATS.includes(value?.type))
      )
  })
});

const handleSubmit = (values, { props, setSubmitting }) => {
  const { onSubmit } = props;
  onSubmit(values, setSubmitting);
};

export default withFormik({
  mapPropsToValues: props => setInitialValues(props),
  validationSchema,
  handleSubmit,
  enableReinitialize: true,
  validateOnMount: false
})(RiskPreventionDocumentList);
